'use client';

import { ReactNode, createContext, useContext, useState } from 'react';

import { Modal, ModalProps } from '@repo/ui/components/modal';

export interface ModalItem<TInnerComponent extends 'div' | 'form' = 'div'> extends Pick<
  ModalProps<TInnerComponent>,
  'footer' | 'header' | 'height' | 'innerComponent' | 'innerComponentProps' | 'showTopCloseButton' | 'width'
> {
  id: string;
  content: ReactNode;
}

interface ModalContextType {
  hideModal: (id: string) => void;
  showModal: (modalProps: ModalItem<'div'> | ModalItem<'form'>) => () => void;
  updateModal: (id: string, updatedModalProps: Partial<ModalItem<'div'>> | Partial<ModalItem<'form'>>) => void;
}

interface ModalProviderProps {
  children: ReactNode;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modals, setModals] = useState<(ModalItem<'div'> | ModalItem<'form'>)[]>([]);

  const showModal = (modalProps: ModalItem<'div'> | ModalItem<'form'>) => {
    setModals((prev) => [...prev, modalProps]);
    return () => setModals((prev) => prev.filter((modal) => modal.id !== modalProps.id));
  };

  const updateModal = (id: string, updatedModalProps: Partial<ModalItem<'div'>> | Partial<ModalItem<'form'>>) => {
    setModals((prev) =>
      prev.map((modal) =>
        modal.id === id ? ({ ...modal, ...updatedModalProps } as ModalItem<'div'> | ModalItem<'form'>) : modal,
      ),
    );
  };

  const hideModal = (id: string) => {
    setModals((prev) => prev.filter((modal) => modal.id !== id));
  };

  return (
    <ModalContext.Provider value={{ hideModal, showModal, updateModal }}>
      {children}
      {modals.map(({ id, content, ...props }) => (
        <Modal
          key={id}
          open={true}
          onClose={() => hideModal(id)}
          data-testid={`modal-${id}`}
          {...props}
        >
          {content}
        </Modal>
      ))}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};
